import i18next from 'i18next';
import { ReactComponent as PendingIcon } from 'assets/icons/pending.svg';
import { ReactComponent as ShippedIcon } from 'assets/icons/shipped.svg';
import { ReactComponent as ProcessingIcon } from 'assets/icons/processing.svg';
import { ReactComponent as HoldIcon } from 'assets/icons/hold.svg';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import permissions from '../../config/permissions';
import {
  CUSTOM_TAGS_OPTIONS,
  PATIENT_CUSTOM_TAGS_OPTIONS,
  SURVEY_TAGS_OPTIONS,
} from '../Shared/CustomTags';
import { INFO_PANE_PAGES } from '../../redux_store';
import FILTER_TYPES from '../Shared/Table/widgets/Filters/constants';

export const ASSESSMENT_STATUSES = {
  invited: 'invited',
  created: 'created',
  inProgress: 'inProgress',
  pendingReview: 'pendingReview',
  partialProcessed: 'partialProcessed',
  completed: 'completed',
  processing: 'processing',
  scheduled: 'scheduled',
  eTransfer: 'eTransfer',
  canceled: 'canceled',
  noShow: 'noShow',
};

// Identifies the assessment that feeds the CPT 99401 Preventive Counseling report.
// Substring match: both the real and the demo "PHQ-GAD16 Health Assessment" qualify.
// TODO: switch to a stable survey type/key once the API exposes one (per backend).
export const PREVENTIVE_COUNSELING_SURVEY_NAME_TOKEN = 'PHQ-GAD16';

export const canDownloadCounselingReport = assessment =>
  Boolean(assessment.surveyName?.includes(PREVENTIVE_COUNSELING_SURVEY_NAME_TOKEN)) &&
  (assessment.status === ASSESSMENT_STATUSES.completed ||
    assessment.status === ASSESSMENT_STATUSES.eTransfer);

// Client-side fallback filename (used only when the server omits the Attachment-Name
// header). ISO date keeps it slash-free and lexicographically sortable.
export const buildCounselingReportFileName = assessment => {
  const date = new Date().toISOString().slice(0, 10);
  const patient = `${assessment.patientLastName ?? ''} ${assessment.patientFirstName ?? ''}`.trim();
  return patient
    ? `Counseling Report - ${patient} - ${date}.pdf`
    : `Counseling Report - ${date}.pdf`;
};

export const ASSESSMENT_STATUSES_OPTIONS = [
  {
    label: i18next.t('tables:invited'),
    value: ASSESSMENT_STATUSES.invited,
    icon: HoldIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:created'),
    value: ASSESSMENT_STATUSES.created,
    icon: HoldIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:inProgress'),
    value: ASSESSMENT_STATUSES.inProgress,
    icon: PendingIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:scheduled'),
    value: ASSESSMENT_STATUSES.scheduled,
    icon: ProcessingIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:pendingReview'),
    value: ASSESSMENT_STATUSES.pendingReview,
    icon: ProcessingIcon,
    enabledForNonSponsors: true,
    addDivider: true,
  },
  {
    label: i18next.t('tables:partialProcessed'),
    value: ASSESSMENT_STATUSES.partialProcessed,
    icon: ProcessingIcon,
    enabledForNonSponsors: true,
    addDivider: true,
  },
  {
    label: i18next.t('tables:completed'),
    value: ASSESSMENT_STATUSES.completed,
    icon: ShippedIcon,
    enabledForNonSponsors: true,
  },
  {
    label: i18next.t('tables:processing'),
    value: ASSESSMENT_STATUSES.processing,
    icon: ShippedIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:eTransfer'),
    value: ASSESSMENT_STATUSES.eTransfer,
    icon: ShippedIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:canceled'),
    value: ASSESSMENT_STATUSES.canceled,
    icon: ShippedIcon,
    enabledForNonSponsors: false,
  },
  {
    label: i18next.t('tables:noShow'),
    value: ASSESSMENT_STATUSES.noShow,
    icon: VisibilityOffIcon,
    enabledForNonSponsors: false,
  },
];

// Filter-dropdown subset of ASSESSMENT_STATUSES_OPTIONS. Excludes
// `noShow` because the assessments table now has a dedicated
// `noShowStatus` column for that flag — keeping it in the Status
// filter would duplicate the control. The full options array is
// still exported for label lookups (CSV export, row actions,
// bot-activity table) where historical `status === 'noShow'` rows
// must still render with a label.
export const ASSESSMENT_STATUSES_FILTER_OPTIONS = ASSESSMENT_STATUSES_OPTIONS.filter(
  option => option.value !== ASSESSMENT_STATUSES.noShow
);

// Separate backend column added alongside `status`. Tracks whether
// the patient was marked as a no-show independently of the
// assessment's lifecycle status. Two values: `none` (default — not
// flagged) and `noShow`.
export const NO_SHOW_STATUSES = {
  none: 'none',
  noShow: 'noShow',
};

export const NO_SHOW_STATUSES_OPTIONS = [
  {
    label: i18next.t('tables:notUpdated'),
    value: NO_SHOW_STATUSES.none,
  },
  {
    label: i18next.t('tables:noShow'),
    value: NO_SHOW_STATUSES.noShow,
    icon: VisibilityOffIcon,
  },
];

export const ASSESSMENT_FILTER_LIST = [
  {
    title: 'All',
    id: 'all',
  },
  {
    title: 'Respiratory & Allergy',
    id: 'respiratoryAndAllergy',
  },
  {
    title: 'Emotional & Psychological',
    id: 'emotionalAndPsychological',
  },
  {
    title: 'Lifestyle & Preventive Care',
    id: 'lifestyleAndPreventiveCare',
  },
  {
    title: 'Chronic Pain',
    id: 'chronicPain',
  },
];

export const GET_ASSESSMENTS_COLUMNS = isPatientPage => [
  {
    size: 200,
    header: i18next.t('tables:customTags'),
    accessorKey: 'accountCustomTags',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: CUSTOM_TAGS_OPTIONS,
    },
    cell: {
      withBubbles: true,
    },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 200,
    header: i18next.t('tables:surveyTags'),
    accessorKey: 'surveyTags',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    cell: {
      withBubbles: true,
    },
  },
  {
    size: 220,
    header: i18next.t('tables:account'),
    accessorKey: 'accountBusinessName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 260,
    header: i18next.t('tables:patientCustomTags'),
    accessorKey: 'patientCustomTags',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: PATIENT_CUSTOM_TAGS_OPTIONS,
    },
    cell: {
      withBubbles: true,
    },
    permission: permissions.ACCOUNTS_GET,
  },
  {
    size: 180,
    header: i18next.t('tables:customId'),
    accessorKey: 'accountCustomIdentifier',
    filter: {},
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:mrnNumber'),
    accessorKey: 'patientMrnNumber',
    filter: {},
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientFirstName'),
    accessorKey: 'patientFirstName',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientLastName'),
    accessorKey: 'patientLastName',
    filter: {},
    hidden: isPatientPage,
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:patientDOB'),
    accessorKey: 'patientDateOfBirth',
    hidden: isPatientPage,
    filter: { filterType: FILTER_TYPES.DATE },
    isDefaultCell: true,
  },
  {
    size: 230,
    header: i18next.t('tables:assessmentName'),
    accessorKey: 'surveyName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:orderDate'),
    accessorKey: 'orderDate',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 250,
    header: i18next.t('tables:appointmentTimestamp'),
    accessorKey: 'appointmentTimestamp',
    filter: {
      shouldRange: true,
      hasNoMaxDate: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:invitedTimestamp'),
    accessorKey: 'invitedTimestamp',
    filter: {
      shouldRange: true,
      hasNoMaxDate: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:createdBy'),
    accessorKey: 'createdBy',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:status'),
    accessorKey: 'status',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: ASSESSMENT_STATUSES_FILTER_OPTIONS,
    },
    cell: {
      withBubble: true,
    },
  },
  {
    size: 180,
    header: i18next.t('tables:noShowStatus'),
    accessorKey: 'noShowStatus',
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: NO_SHOW_STATUSES_OPTIONS,
    },
    cell: {
      withBubble: true,
    },
  },
];

export const INVITATION_TYPE = {
  send: 'send',
  resend: 'resend',
  notAllowed: 'notAllowed',
};
